// frontend/src/pages/LocationDetailPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet'; // ✨ Thêm Polyline, useMap
import { 
  FaHeart, FaRegHeart, FaUtensils, FaTimes, 
  FaMapMarkerAlt, FaCamera, FaTrash 
} from 'react-icons/fa';

import API from '../api';
import AddImageModal from '../pages/AddImageModal';
import { useAuth } from '../context/AuthContext';
import { useLocationContext } from '../context/LocationContext'; // ✨ Import Context vị trí

import 'leaflet/dist/leaflet.css';
import L from 'leaflet'; 
import './LocationDetailPage.css';

// --- CẤU HÌNH LEAFLET ---
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Icon vị trí người dùng (để phân biệt với địa điểm quán)
const userLocationIcon = new L.Icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
});

// --- HELPER: Tự động zoom bản đồ bao trọn tuyến đường ---
const FitBoundsToRoute = ({ route }) => {
    const map = useMap();
    useEffect(() => {
      if (route && route.length > 0) {
        map.fitBounds(route, { padding: [50, 50] });
      }
    }, [route, map]);
    return null;
};

// --- HELPER PLACEHOLDER ---
const getPlaceholderStyle = (name) => {
  const gradients = ['linear-gradient(135deg, #FF9A9E 0%, #FECFEF 100%)', 'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)', 'linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)', 'linear-gradient(120deg, #e0c3fc 0%, #8ec5fc 100%)', 'linear-gradient(120deg, #f093fb 0%, #f5576c 100%)', 'linear-gradient(120deg, #f6d365 0%, #fda085 100%)', 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'];
  let hash = 0;
  if (name) { for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash); }
  return { background: gradients[Math.abs(hash) % gradients.length] };
};

const LocationDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, userRole } = useAuth();
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  
  // ✨ Lấy vị trí người dùng từ Context
  const { location: userLocation } = useLocationContext();

  // --- STATE ---
  const [location, setLocation] = useState(null);
  const [reviews, setReviews] = useState([]); 
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isFavorited, setIsFavorited] = useState(false);
  
  // State Modals
  const [showMenuModal, setShowMenuModal] = useState(false); 
  const [menuItems, setMenuItems] = useState([]); 
  const [loadingMenu, setLoadingMenu] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);

  // State Lightbox
  const [clickedImg, setClickedImg] = useState(null);

  // State Reviews
  const [userRating, setUserRating] = useState(5);
  const [userComment, setUserComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // ✨ STATE ROUTING (Đường đi)
  const [routeCoordinates, setRouteCoordinates] = useState(null);
  const [routeInfo, setRouteInfo] = useState(null); // { distance, duration }
  const [isFetchingRoute, setIsFetchingRoute] = useState(false);

  // --- CHECK QUYỀN ---
  const isAdmin = userRole === 'admin' && queryParams.get('view') !== 'user';
  const isResident = user && userRole === 'user';
  const isUser = userRole === 'user' || queryParams.get('view') === 'user';

  // --- FETCH DATA ---
  const fetchData = async () => {
    try {
      const locRes = await API.get(`/locations/${id}`);
      setLocation(locRes.data.data);
      const revRes = await API.get(`/reviews`, { params: { location_id: id } });
      setReviews(revRes.data.data || []);
    } catch (err) {
      console.error("Lỗi tải dữ liệu:", err);
      if (!location) setError("Không thể tải thông tin địa điểm.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (id) fetchData(); }, [id]);

  useEffect(() => {
    if (user && id) {
      API.get(`/favorites/check?location_id=${id}`).then(res => setIsFavorited(res.data.isFavorited)).catch(err => console.error(err));
    }
  }, [user, id]);

  // --- HANDLERS ---
  const handleDeleteImage = async (imageId, e) => {
      e.stopPropagation();
      if (!window.confirm("Bạn chắc chắn muốn xóa ảnh này?")) return;
      try {
          await API.delete(`/locations/images/${imageId}`);
          const updatedImages = location.images.filter(img => img.id !== imageId);
          setLocation({ ...location, images: updatedImages });
          alert("Đã xóa ảnh thành công!");
      } catch (err) {
          alert("Lỗi xóa ảnh: " + (err.response?.data?.message || err.message));
      }
  };

  const handleOpenMenu = async () => { setShowMenuModal(true); if (menuItems.length === 0) { setLoadingMenu(true); try { const res = await API.get(`/locations/${id}/menu`); setMenuItems(res.data.data); } catch (err) { console.error(err); } finally { setLoadingMenu(false); } } };
  const handlePostReview = async (e) => { e.preventDefault(); if (!isResident) return alert("Chỉ Cư dân mới được đánh giá!"); if (!userComment.trim()) return alert("Vui lòng nhập nội dung!"); setSubmitting(true); try { await API.post('/reviews', { location_id: parseInt(id), user_id: user.id, rating: userRating, comment: userComment, review_type: 'location' }); alert("Cảm ơn đánh giá!"); setUserComment(''); setUserRating(5); fetchData(); } catch (err) { alert(err.response?.data?.message || "Lỗi gửi đánh giá."); } finally { setSubmitting(false); } };
  const handleToggleFavorite = async () => { if (!user) return alert("Vui lòng đăng nhập!"); try { setIsFavorited(!isFavorited); await API.post('/favorites/toggle', { location_id: id }); } catch (err) { setIsFavorited(!isFavorited); alert("Lỗi kết nối!"); } };
  const handleUploadSuccess = () => { fetchData(); };

  // ✨ HÀM LẤY CHỈ ĐƯỜNG KHI BẤM VÀO MARKER
  const handleGetDirections = async () => {
      if (!userLocation.coordinates || !userLocation.coordinates.lat) {
          alert("Không tìm thấy vị trí hiện tại của bạn. Vui lòng bật định vị.");
          return;
      }

      const start = userLocation.coordinates; // {lat, lng}
      const end = { lat: location.latitude, lng: location.longitude };
      
      const ORS_API_KEY = 'eyJvcmciOiI1YjNjZTM1OTc4NTExMTAwMDFjZjYyNDgiLCJpZCI6ImQ5ZjA5NTk2MzBkMDRkYmM4MDM0OWQ5MTUyYmEwYzQ5IiwiaCI6Im11cm11cjY0In0='; 

      setIsFetchingRoute(true);
      setRouteCoordinates(null);

      // Mặc định dùng driving-car cho mini map
      const profile = 'driving-car'; 
      const url = `https://api.openrouteservice.org/v2/directions/${profile}?api_key=${ORS_API_KEY}&start=${start.lng},${start.lat}&end=${end.lng},${end.lat}`;

      try {
        const response = await fetch(url);
        const data = await response.json();

        if (data.features && data.features.length > 0) {
          const feature = data.features[0];
          // Geometry trả về là [lng, lat], cần đảo ngược cho Leaflet [lat, lng]
          const coords = feature.geometry.coordinates.map(coord => [coord[1], coord[0]]);
          setRouteCoordinates(coords);
          
          if (feature.properties.summary) {
              setRouteInfo(feature.properties.summary);
          }
        } else {
          alert("Không tìm thấy đường đi.");
        }
      } catch (error) {
        console.error("Lỗi khi lấy chỉ đường:", error);
        alert("Đã xảy ra lỗi khi tìm đường.");
      } finally {
        setIsFetchingRoute(false);
      }
  };

  // Helper format
  const formatDistance = (meters) => `${(meters / 1000).toFixed(1)} km`;
  const formatDuration = (seconds) => {
      const minutes = Math.round(seconds / 60);
      return minutes < 60 ? `${minutes} phút` : `${Math.floor(minutes/60)} giờ ${minutes%60} phút`;
  };

  // --- RENDER ---
  if (loading) return <div className="detail-page-loading">⏳ Đang tải...</div>;
  if (error) return <div className="detail-page-error">❌ {error}</div>;
  if (!location) return null;

  const position = [location.latitude, location.longitude];
  const displayImages = location.images || location.gallery || [];

  return (
    <div className="location-detail-page">
      <div className="detail-header">
        <button onClick={() => navigate(-1)} className="back-button">&larr; Quay lại</button>
        <div className="title-section">
            <h1>{location.name}</h1>
            <div className="action-buttons">
                {isUser && (<button onClick={handleToggleFavorite} className={`action-btn fav-btn ${isFavorited ? 'active' : ''}`}>{isFavorited ? <FaHeart /> : <FaRegHeart />}</button>)}
                <button onClick={handleOpenMenu} className="action-btn menu-btn"><FaUtensils /> Xem thực đơn</button>
                <button onClick={() => setShowUploadModal(true)} className="action-btn upload-btn"><FaCamera /> Thêm ảnh</button>
            </div>
        </div>
      </div>

      <div className="detail-content-layout">
        <div className="detail-info-panel">
          <h3>Thông tin chi tiết</h3>
          <p><strong>📍 Địa chỉ:</strong> {location.address}, {location.district}</p>
          {location.description && <p><strong>📝 Mô tả:</strong> {location.description}</p>}
          <p><strong>⭐ Đánh giá:</strong> {location.average_rating ? Number(location.average_rating).toFixed(1) : 'Chưa có'} ({location.review_count || 0} lượt)</p>
          <p><strong>💰 Khoảng giá:</strong> {location.min_price?.toLocaleString()}đ - {location.max_price?.toLocaleString()}đ</p>
          {isAdmin && (<p style={{marginTop: 15}}><strong>Trạng thái:</strong> <span className={`status-badge ${location.is_approved ? 'approved' : 'pending'}`}>{location.is_approved ? 'Đã duyệt' : 'Chờ duyệt'}</span></p>)}

          {displayImages.length > 0 && (
            <div className="detail-gallery-section">
                <h4>📷 Hình ảnh ({displayImages.length})</h4>
                <div className="image-gallery-container">
                    {displayImages.map((image, index) => {
                        const imgSrc = image.url || image;
                        const imgId = image.id;
                        return (
                          <div key={imgId || index} className="gallery-img-wrapper" onClick={() => setClickedImg(imgSrc)} style={{cursor: 'pointer'}}>
                              <img src={imgSrc} alt={`Ảnh ${index}`} className="gallery-image" onError={(e) => { e.target.style.display = 'none'; if(e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }} />
                              <div className="gallery-placeholder" style={{...getPlaceholderStyle(location.name), display: 'none'}}>{location.name.charAt(0).toUpperCase()}</div>
                              {isAdmin && imgId && (<button className="btn-delete-image" onClick={(e) => handleDeleteImage(imgId, e)} title="Xóa ảnh này"><FaTrash /></button>)}
                          </div>
                        );
                    })}
                </div>
            </div>
          )}
        </div>
        
        {/* MAP PANEL */}
        <div className="detail-map-panel">
          {/* ✨ Hiển thị thông tin đường đi nếu có */}
          {routeInfo && (
              <div className="mini-route-info" style={{marginBottom: '5px', fontSize: '0.9rem', color: '#2c3e50'}}>
                  <strong>🚗 {formatDistance(routeInfo.distance)}</strong> - {formatDuration(routeInfo.duration)}
              </div>
          )}
          {isFetchingRoute && <div style={{fontSize:'0.8rem', color: '#666'}}>🔄 Đang tìm đường...</div>}

          <MapContainer center={position} zoom={16} scrollWheelZoom={false} className="detail-map">
            <TileLayer
              url="https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}"
              attribution="Google Maps"
            />
            
            {/* ✨ Component tự động zoom khi có đường */}
            <FitBoundsToRoute route={routeCoordinates} />

            {/* Marker của địa điểm quán */}
            <Marker 
                position={position}
                eventHandlers={{
                    click: handleGetDirections // ✨ Bấm vào để lấy chỉ đường
                }}
            >
              <Popup>
                  <b>{location.name}</b><br/>
                  <span style={{fontSize:'0.8em', color:'blue', cursor:'pointer'}} onClick={handleGetDirections}>
                      (Bấm để chỉ đường)
                  </span>
              </Popup>
            </Marker>

            {/* ✨ Marker vị trí người dùng (chỉ hiện khi đã có tọa độ) */}
            {userLocation.coordinates && userLocation.coordinates.lat && (
                <Marker 
                    position={[userLocation.coordinates.lat, userLocation.coordinates.lng]}
                    icon={userLocationIcon}
                >
                    <Popup>Vị trí của bạn</Popup>
                </Marker>
            )}

            {/* ✨ Vẽ đường đi */}
            {routeCoordinates && <Polyline positions={routeCoordinates} color="#3498db" weight={5} />}

          </MapContainer>
        </div>
      </div>

      <div className="reviews-section">
        <h4>⭐ Đánh giá từ cộng đồng ({reviews.length})</h4>
        {isResident ? (
          <form className="review-form" onSubmit={handlePostReview}>
            <div className="rating-select">{[1, 2, 3, 4, 5].map(star => (<span key={star} className={`star ${star <= userRating ? 'active' : ''}`} onClick={() => setUserRating(star)}>★</span>))}<span className="rating-text">({userRating} sao)</span></div>
            <textarea className="review-textarea" placeholder="Chia sẻ trải nghiệm..." value={userComment} onChange={e => setUserComment(e.target.value)} />
            <button type="submit" className="btn-submit-review" disabled={submitting}>{submitting ? 'Đang gửi...' : 'Gửi đánh giá'}</button>
          </form>
        ) : (<div className="login-prompt">{userRole === 'admin' ? "⚠️ Admin không thể đánh giá." : "Vui lòng đăng nhập Cư dân để đánh giá."}</div>)}
        <div className="review-list">{reviews.length === 0 ? <p className="no-reviews">Chưa có đánh giá.</p> : reviews.map((rev) => (<div key={rev.id} className="review-item"><div className="review-header"><div className="reviewer-info"><div className="reviewer-avatar"><div className="reviewer-placeholder" style={getPlaceholderStyle(rev.authorName)}>{rev.authorName?.charAt(0).toUpperCase()}</div></div><div><div className="reviewer-name">{rev.authorName}</div><div className="review-date">{rev.timeAgo}</div></div></div><div className="review-rating">{"⭐".repeat(rev.rating)}</div></div><div className="review-comment">{rev.comment}</div></div>))}</div>
      </div>

      {showMenuModal && (<div className="menu-modal-overlay" onClick={() => setShowMenuModal(false)}><div className="menu-modal-content" onClick={(e) => e.stopPropagation()}><div className="menu-modal-header"><h2>📜 Thực đơn: {location.name}</h2><button className="close-modal-btn" onClick={() => setShowMenuModal(false)}><FaTimes /></button></div><div className="menu-modal-body">{loadingMenu ? (<div className="menu-loading">⏳ Tải menu...</div>) : menuItems.length === 0 ? (<div className="menu-empty">Chưa có menu.</div>) : (<div className="menu-grid-display">{menuItems.map((item) => (<div key={item.id} className="menu-item-display"><div className="menu-item-info"><h4>{item.custom_name || item.base_dish_name}</h4><p className="menu-desc">{item.description}</p></div><div className="menu-item-price">{Number(item.price).toLocaleString()}đ</div></div>))}</div>)}</div></div></div>)}
      {showUploadModal && (<AddImageModal locationId={id} onClose={() => setShowUploadModal(false)} onSuccess={handleUploadSuccess} />)}

      {clickedImg && (
        <div className="lightbox-overlay" onClick={() => setClickedImg(null)}>
            <span className="lightbox-close" onClick={() => setClickedImg(null)}><FaTimes /></span>
            <img src={clickedImg} alt="Full screen" className="lightbox-image" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
};

export default LocationDetailPage;